import React from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, ChevronDown, GraduationCap, Handshake, Plus, Settings2, Sparkles, Trash2, Users } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { IconPicker } from '@/components/ui/icon-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { filterPillClass } from '@/components/ui/filter-pills'
import { FormField } from '@/components/features/bookings/FormField'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import type { MemberField } from '@/lib/sdk-official/resources/member-import'
import type { SetupFieldsProps } from '@/modules/types'
import { NetworkingHostRuleSettings } from './NetworkingHostRuleSettings'
import type { NetworkingBadge, NetworkingConfig, NetworkingPreset } from '@/types/networking'
import { ALL_BADGES, DURATION_CHOICES, PRESETS, categoryLabel, keyOf, presetConfig, sideLabel, type NetworkingConfigErrors } from './networkingConfig'

const PRESET_ICONS: Record<NetworkingPreset, typeof Users> = {
  COMMUNITY_MENTORING: GraduationCap,
  COMPANY_MENTORING: Building2,
  BUSINESS: Handshake,
  EVENT: Sparkles,
  CUSTOM: Settings2
}
const FILTERABLE = ['TEXT', 'SELECT', 'MULTI_SELECT', 'BOOLEAN']

/** A short enum as pills, the first one never empty (PADROES-UI sections 9 and 16). */
function Choice<V extends string>({ label, value, options, onChange, disabled, hint, error, testId }: {
  label: string; value: V; options: Array<{ value: V; label: string }>; onChange: (v: V) => void; disabled?: boolean; hint?: string; error?: string; testId?: string
}) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2 px-0.5">
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} disabled={disabled} className={filterPillClass(value === o.value)} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
      {error ? <p role="alert" className="text-xs text-red-600">{error}</p> : hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
    </div>
  )
}

const Group = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <fieldset className="space-y-4 rounded-lg border border-gray-200 p-4">
    <legend className="px-1 text-sm font-semibold text-gray-900">{title}</legend>
    {children}
  </fieldset>
)

const numberOrNull = (raw: string): number | null => (raw.trim() === '' ? null : Math.max(0, Math.floor(Number(raw))))

/**
 * The Networking setup (PROPOSTA-NETWORKING sections 2.5 and 3): the model
 * first, then every option it filled, folded until someone wants to change one.
 * The creation wizard asks it as a step of its own and the space settings show
 * this very component (PADROES-UI section 14).
 */
export const NetworkingSpaceFields = ({ config: raw, onChange, errors: rawErrors, disabled = false, context, spaceId }: SetupFieldsProps) => {
  const { t } = useTranslation('networking')
  const config = raw as NetworkingConfig
  const errors = React.useMemo(() => (rawErrors || {}) as NetworkingConfigErrors, [rawErrors])
  const [open, setOpen] = React.useState(() => Object.keys(errors).length > 0)
  const [fields, setFields] = React.useState<MemberField[] | null>(null)

  React.useEffect(() => { if (Object.keys(errors).length) setOpen(true) }, [errors])
  React.useEffect(() => {
    if (!open || fields) return
    let alive = true
    sdk.memberFields.list()
      .then((list) => { if (alive) setFields((list || []).filter((f) => FILTERABLE.includes(f.type) && f.profileVisibility !== 'NEVER')) })
      .catch(() => { if (alive) setFields([]) })
    return () => { alive = false }
  }, [open, fields])

  const set = (patch: Partial<NetworkingConfig>) => onChange({ ...config, ...patch })
  const pickPreset = (preset: NetworkingPreset) => {
    if (preset === config.preset) return
    /* The model refills the options; the categories and filters someone typed stay. */
    onChange({ ...presetConfig(preset), sideLabels: config.sideLabels, taxonomy: config.taxonomy })
  }
  const symmetric = config.sides === 'SYMMETRIC'
  const toggleDuration = (minutes: number) => {
    const next = config.durations.includes(minutes) ? config.durations.filter((d) => d !== minutes) : [...config.durations, minutes].sort((a, b) => a - b)
    set({ durations: next, defaultDuration: next.includes(config.defaultDuration) ? config.defaultDuration : (next[0] ?? config.defaultDuration) })
  }
  const toggleBadge = (badge: NetworkingBadge, on: boolean) =>
    set({ badges: { ...config.badges, enabled: on ? [...config.badges.enabled, badge] : config.badges.enabled.filter((b) => b !== badge) } })
  const categories = config.taxonomy.categories
  const setCategories = (next: typeof categories) => set({ taxonomy: { ...config.taxonomy, categories: next } })
  const addCategory = () => {
    let n = categories.length + 1
    while (categories.some((c) => c.key === `category-${n}`)) n += 1
    setCategories([...categories, { key: `category-${n}`, icon: 'tag', label: '' }])
  }
  const toggleField = (id: string) => {
    const ids = config.taxonomy.fieldIds
    set({ taxonomy: { ...config.taxonomy, fieldIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] } })
  }
  const limit = (key: keyof NetworkingConfig['limits'], value: number | null) => set({ limits: { ...config.limits, [key]: value } })
  const durationsShown = Array.from(new Set([...DURATION_CHOICES, ...config.durations])).sort((a, b) => a - b)

  return (
    <div className="space-y-5" data-testid="networking-setup">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">{t('config.model')}</p>
        <div role="radiogroup" aria-label={t('config.model')} className="grid gap-2 sm:grid-cols-2">
          {PRESETS.map((preset) => {
            const Icon = PRESET_ICONS[preset]
            const active = config.preset === preset
            return (
              <button
                key={preset} type="button" role="radio" aria-checked={active} disabled={disabled} data-testid={`networking-preset-${preset}`}
                onClick={() => pickPreset(preset)}
                className={cn('flex items-start gap-3 rounded-lg border p-3 text-left transition-colors', active ? 'border-primary bg-primary-50 ring-1 ring-primary' : 'border-gray-200 hover:border-gray-300')}
              >
                <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', active ? 'text-primary' : 'text-gray-400')} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{t(`presets.${preset}.name`)}</span>
                  <span className="block text-xs text-gray-500">{t(`presets.${preset}.description`)}</span>
                </span>
              </button>
            )
          })}
        </div>
        <p className="text-xs text-gray-500">{t('config.modelHint')}</p>
      </div>

      <button type="button" className="flex w-full items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100" aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid="networking-options-toggle">
        <span>{t('config.adjust')}</span>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="space-y-4" data-testid="networking-options">
          <Group title={t('config.groups.entry')}>
            <Choice label={t('config.sides')} value={config.sides} disabled={disabled} testId="networking-sides"
              options={(['MENTORING', 'SYMMETRIC'] as const).map((v) => ({ value: v, label: t(`config.sidesOption.${v}`) }))}
              hint={t(`config.sidesHint.${config.sides}`)}
              onChange={(v) => set({ sides: v })} />
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label={t('config.hostLabel')} htmlFor="nw-host-label" error={errors['sideLabels.host']}>
                <Input id="nw-host-label" maxLength={40} disabled={disabled} value={config.sideLabels.host ?? ''} placeholder={t(`sides.${config.sides}.host`)}
                  onChange={(e) => set({ sideLabels: { ...config.sideLabels, host: e.target.value.trim() ? e.target.value : null } })} />
              </FormField>
              <FormField label={t('config.participantLabel')} htmlFor="nw-participant-label" error={errors['sideLabels.participant']}>
                <Input id="nw-participant-label" maxLength={40} disabled={disabled} value={config.sideLabels.participant ?? ''} placeholder={t(`sides.${config.sides}.participant`)}
                  onChange={(e) => set({ sideLabels: { ...config.sideLabels, participant: e.target.value.trim() ? e.target.value : null } })} />
              </FormField>
            </div>
            <FormField label={t('config.offerers', { host: sideLabel(t, config, 'host') })} htmlFor="nw-offerers" error={errors.offerers}>
              <Select value={config.offerers} disabled={disabled} onValueChange={(v) => set({ offerers: v as NetworkingConfig['offerers'] })}>
                <SelectTrigger id="nw-offerers"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(['MEMBERS', 'TEAM', 'ADMINS'] as const).map((v) => <SelectItem key={v} value={v}>{t(`config.offerersOption.${v}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <Choice label={symmetric ? t('config.joinSymmetric') : t('config.hostJoin', { host: sideLabel(t, config, 'host') })} value={config.hostJoin} disabled={disabled} testId="networking-host-join"
              options={(['OPEN', 'APPROVAL', 'INVITE'] as const).map((v) => ({ value: v, label: t(`config.joinOption.${v}`) }))}
              hint={t(`config.joinHint.${config.hostJoin}`)} error={errors.hostJoin}
              onChange={(v) => set({ hostJoin: v })} />
            {context === 'edit' && spaceId && <NetworkingHostRuleSettings spaceId={spaceId} host={sideLabel(t, config, 'host')} disabled={disabled} />}
            {/* 2026-10-29: whoever opens the space is a mentee; the space's access decides who that is. */}
            {!symmetric && <p className="text-xs text-gray-500" data-testid="networking-participants-by-access">{t('config.participantsByAccess', { participant: sideLabel(t, config, 'participant') })}</p>}
          </Group>

          <Group title={t('config.groups.sessions')}>
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-gray-700">{t('config.durations')}</p>
              <div className="flex flex-wrap gap-2 px-0.5" role="group" aria-label={t('config.durations')}>
                {durationsShown.map((m) => (
                  <button key={m} type="button" aria-pressed={config.durations.includes(m)} disabled={disabled} className={filterPillClass(config.durations.includes(m))} onClick={() => toggleDuration(m)}>
                    {t('common.minutes', { count: m })}
                  </button>
                ))}
              </div>
              {errors.durations && <p role="alert" className="text-xs text-red-600">{errors.durations}</p>}
            </div>
            {config.durations.length > 1 && (
              <FormField label={t('config.defaultDuration')} htmlFor="nw-default-duration" error={errors.defaultDuration}>
                <Select value={String(config.defaultDuration)} disabled={disabled} onValueChange={(v) => set({ defaultDuration: Number(v) })}>
                  <SelectTrigger id="nw-default-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.durations.map((m) => <SelectItem key={m} value={String(m)}>{t('common.minutes', { count: m })}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
            )}
            <Choice label={t('config.requestApproval')} value={config.requestApproval} disabled={disabled} testId="networking-request-approval"
              options={(['HOST_CHOOSES', 'ALWAYS', 'NEVER'] as const).map((v) => ({ value: v, label: t(`config.requestApprovalOption.${v}`) }))}
              hint={t(`config.requestApprovalHint.${config.requestApproval}`)} error={errors.requestApproval}
              onChange={(v) => set({ requestApproval: v })} />
            <Choice label={t('config.location')} value={config.location} disabled={disabled}
              options={(['ONLINE', 'IN_PERSON', 'HOST_CHOOSES'] as const).map((v) => ({ value: v, label: t(`config.locationOption.${v}`) }))}
              error={errors.location} onChange={(v) => set({ location: v })} />
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label={t('config.limits.sessionsPerWeek')} htmlFor="nw-week" hint={t('config.limits.emptyIsNone')} error={errors['limits.sessionsPerWeek']}>
                <Input id="nw-week" type="number" min={0} max={100} inputMode="numeric" disabled={disabled} value={config.limits.sessionsPerWeek ?? ''} onChange={(e) => limit('sessionsPerWeek', numberOrNull(e.target.value))} data-testid="networking-limit-week" />
              </FormField>
              <FormField label={t('config.limits.sessionsPerPairPerMonth')} htmlFor="nw-pair" hint={t('config.limits.emptyIsNone')} error={errors['limits.sessionsPerPairPerMonth']}>
                <Input id="nw-pair" type="number" min={0} max={100} inputMode="numeric" disabled={disabled} value={config.limits.sessionsPerPairPerMonth ?? ''} onChange={(e) => limit('sessionsPerPairPerMonth', numberOrNull(e.target.value))} />
              </FormField>
              <FormField label={t('config.limits.minNoticeHours')} htmlFor="nw-notice" hint={t('config.limits.emptyIsNone')} error={errors['limits.minNoticeHours']}>
                <Input id="nw-notice" type="number" min={0} max={720} inputMode="numeric" disabled={disabled} value={config.limits.minNoticeHours ?? ''} onChange={(e) => limit('minNoticeHours', numberOrNull(e.target.value))} />
              </FormField>
              <FormField label={t('config.limits.horizonDays')} htmlFor="nw-horizon" error={errors['limits.horizonDays']}>
                <Input id="nw-horizon" type="number" min={1} max={365} inputMode="numeric" disabled={disabled} value={config.limits.horizonDays} onChange={(e) => limit('horizonDays', numberOrNull(e.target.value) ?? 1)} />
              </FormField>
            </div>
          </Group>

          <Group title={t('config.groups.reviews')}>
            <Choice label={t('config.rating')} value={config.rating} disabled={disabled} testId="networking-rating"
              options={(['MUTUAL', 'PARTICIPANT_ONLY', 'ATTENDANCE_ONLY', 'OFF'] as const).map((v) => ({ value: v, label: t(`config.ratingOption.${v}`) }))}
              hint={t(`config.ratingHint.${config.rating}`)} error={errors.rating}
              onChange={(v) => set({ rating: v })} />
            {config.rating !== 'OFF' && config.rating !== 'ATTENDANCE_ONLY' && (
              <Choice label={t('config.reviewsVisibility')} value={config.reviewsVisibility} disabled={disabled} testId="networking-reviews-visibility"
                options={(['COMMUNITY', 'HOST_AND_TEAM', 'TEAM'] as const).map((v) => ({ value: v, label: t(`config.visibilityOption.${v}`) }))}
                hint={t(`config.visibilityHint.${config.reviewsVisibility}`)} error={errors.reviewsVisibility}
                onChange={(v) => set({ reviewsVisibility: v })} />
            )}
          </Group>

          <Group title={t('config.groups.badges')}>
            <div className="space-y-2">
              {ALL_BADGES.map((badge) => {
                const on = config.badges.enabled.includes(badge)
                const blocked = badge === 'TOP_RATED' && config.reviewsVisibility !== 'COMMUNITY'
                return (
                  <div key={badge} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{t(`badges.${badge}`)}</p>
                      <p className="text-xs text-gray-500">{blocked ? t('config.topRatedNeedsCommunity') : t(`config.badgeHint.${badge}`)}</p>
                    </div>
                    <Switch checked={on && !blocked} disabled={disabled || blocked} aria-label={t(`badges.${badge}`)} onCheckedChange={(v) => toggleBadge(badge, v)} />
                  </div>
                )
              })}
            </div>
            {config.badges.enabled.includes('TOP_RATED') && config.reviewsVisibility === 'COMMUNITY' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label={t('config.topRatedMin')} htmlFor="nw-top-min" error={errors['badges.topRated.minRating']}>
                  <Input id="nw-top-min" type="number" step="0.1" min={1} max={5} disabled={disabled} value={config.badges.topRated.minRating}
                    onChange={(e) => set({ badges: { ...config.badges, topRated: { ...config.badges.topRated, minRating: Number(e.target.value) } } })} />
                </FormField>
                <FormField label={t('config.topRatedReviews')} htmlFor="nw-top-reviews" error={errors['badges.topRated.minReviews']}>
                  <Input id="nw-top-reviews" type="number" min={1} max={1000} disabled={disabled} value={config.badges.topRated.minReviews}
                    onChange={(e) => set({ badges: { ...config.badges, topRated: { ...config.badges.topRated, minReviews: Math.max(1, Math.floor(Number(e.target.value))) } } })} />
                </FormField>
              </div>
            )}
          </Group>

          <Group title={t('config.groups.categories')}>
            <p className="text-xs text-gray-500">{t('config.categoriesHint')}</p>
            <ul className="space-y-2">
              {categories.map((c, i) => (
                <li key={`${c.key}-${i}`} className="flex items-start gap-2">
                  <IconPicker value={c.icon || 'tag'} disabled={disabled} className="w-auto flex-shrink-0" onChange={(icon) => setCategories(categories.map((x, j) => (j === i ? { ...x, icon } : x)))} />
                  <div className="min-w-0 flex-1">
                    <Input
                      aria-label={t('config.categoryName')} maxLength={60} disabled={disabled}
                      value={c.label ?? categoryLabel(t, c)}
                      onChange={(e) => {
                        const label = e.target.value
                        /* A new category takes its key from the name; an existing one keeps it (people already chose it). */
                        const fresh = /^category-\d+$/.test(c.key)
                        setCategories(categories.map((x, j) => (j === i ? { ...x, label, key: fresh && keyOf(label) && !categories.some((o, k) => k !== i && o.key === keyOf(label)) ? keyOf(label) : x.key } : x)))
                      }}
                    />
                    {errors[`taxonomy.categories[${i}].key`] && <p role="alert" className="mt-1 text-xs text-red-600">{errors[`taxonomy.categories[${i}].key`]}</p>}
                  </div>
                  <button type="button" disabled={disabled} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-red-600" aria-label={t('config.removeCategory', { name: categoryLabel(t, c) })}
                    onClick={() => setCategories(categories.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
            {categories.length < 30 && (
              <button type="button" disabled={disabled} onClick={addCategory} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                <Plus className="h-4 w-4" />{t('config.addCategory')}
              </button>
            )}
            {errors['taxonomy.categories'] && <p role="alert" className="text-xs text-red-600">{errors['taxonomy.categories']}</p>}
          </Group>

          <Group title={t('config.groups.filters')}>
            <p className="text-xs text-gray-500">{t('config.filtersHint')}</p>
            {fields === null ? (
              <p className="text-sm text-gray-400">{t('common.loading')}</p>
            ) : fields.length === 0 ? (
              <p className="text-sm text-gray-500">{t('config.noFields')}</p>
            ) : (
              <div className="flex flex-wrap gap-2 px-0.5" role="group" aria-label={t('config.groups.filters')}>
                {fields.map((f) => (
                  <button key={f.id} type="button" aria-pressed={config.taxonomy.fieldIds.includes(f.id)} disabled={disabled || (!config.taxonomy.fieldIds.includes(f.id) && config.taxonomy.fieldIds.length >= 10)}
                    className={filterPillClass(config.taxonomy.fieldIds.includes(f.id))} onClick={() => toggleField(f.id)}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            {Object.entries(errors).filter(([k]) => k.startsWith('taxonomy.fieldIds')).slice(0, 1).map(([k, m]) => <p key={k} role="alert" className="text-xs text-red-600">{m}</p>)}
          </Group>

          <p className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">{t('config.alwaysFree')}</p>
        </div>
      )}
    </div>
  )
}
